import ChannelIcon from '@/components/channels/ChannelIcon.vue'
import { app } from '@/composition/app'
import { EventState } from '@/composition/events'
import { GroupState } from '@/composition/groups'
import { LearningState } from '@/composition/learning'
import { GAEventTypes, trackCustomGAEvents } from '@ghl/clientportal-core'
import { isGokollab } from '@ghl/clientportal-core/init'
import {
  BookOpen01Icon,
  CalendarDateIcon,
} from '@gohighlevel/ghl-icons/24/outline'
import DOMPurify from 'dompurify'
import { createApp, h, render } from 'vue'
import { Router } from 'vue-router'
import {
  getCourseUrl,
  getEventUrl,
  isChannelIconEmoji,
  openGlobalUserProfile,
} from './helper'

export const enum MentionType {
  CHANNEL_MENTION = 'channel-mention',
  EVENTS_MENTION = 'events-mention',
  COURSES_MENTION = 'courses-mention',
  USER_MENTION = 'mention', // keep it mention to avoid conflict existing user mention
}

// This method, make path of svg icon to be baseline aligned
const applyBaselineAlignment = (iconHTML: string): string => {
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = iconHTML
  const svg = tempDiv.querySelector('svg')

  if (!svg) return iconHTML

  // Temporarily append to DOM so getBBox() works
  document.body.appendChild(tempDiv)

  try {
    // Get viewBox height (default 24 for most icons)
    let vbH = 24
    const viewBox = svg.getAttribute('viewBox')
    if (viewBox) {
      const parts = viewBox.split(/\s+/).map(Number)
      vbH = parts[3] || vbH
    }

    // Create a group element and move all children into it
    const g = document.createElementNS('http://www.w3.org/2000/svg', 'g')
    g.setAttribute('class', '__bottom_align__')

    // Move all children except defs into the group
    const toMove: ChildNode[] = []
    svg.childNodes.forEach(n => {
      if (
        !(n as Element).tagName ||
        (n as Element).tagName.toLowerCase() !== 'defs'
      ) {
        toMove.push(n)
      }
    })
    toMove.forEach(n => g.appendChild(n))
    svg.appendChild(g)

    // Apply baseline alignment
    try {
      const bbox = g.getBBox()
      const contentBottom = bbox.y + bbox.height
      const delta = vbH - contentBottom - 1
      g.setAttribute('transform', `translate(0, ${delta})`)
      g.setAttribute('vector-effect', 'non-scaling-stroke')
    } catch (e) {
      console.warn('Could not get bbox for icon alignment:', e)
    }
  } finally {
    // Remove from DOM
    document.body.removeChild(tempDiv)
  }

  return tempDiv.innerHTML
}

function generateChannelIconHTML(
  iconName: string,
  iconSize: number = 16
): string {
  const container = document.createElement('div')

  // Create minimal channel object to render the icon
  const channel = {
    icon: iconName,
    showLockIcon: false,
  }
  if (isChannelIconEmoji(iconName)) {
    return `<span class="text-sm leading-none" style="display: inline-block; vertical-align: baseline;">${iconName}</span>`
  }

  const vnode = h(ChannelIcon, {
    iconSize,
    emojiSize: iconSize,
    channel,
    showingInSidebar: false,
    showingInMobileDrawer: false,
  })

  const app = createApp({ render: () => vnode })
  app.mount(container)
  const html = container.innerHTML
  app.unmount()

  // Apply baseline alignment
  return applyBaselineAlignment(html)
}

export function showPreviewTooltip(
  targetElement: Element | null,
  parentElement: Element | null | undefined,
  type: MentionType
): void {
  if (targetElement) {
    const parentElementRect =
      targetElement?.parentElement?.getBoundingClientRect()
    const linkRects = targetElement?.getClientRects()
    let eventName = 'show-tooltip'
    const additionalProps: any = {}
    if (type === MentionType.USER_MENTION) {
      additionalProps.contactId = targetElement.getAttribute('data-id')
    }
    if (type === MentionType.CHANNEL_MENTION) {
      eventName = 'show-private-channel-tooltip'
      additionalProps.channelId = targetElement.getAttribute('data-id')
      // Extract only text content, excluding icon HTML
      const textNodes = Array.from(targetElement.childNodes).filter(
        node => node.nodeType === Node.TEXT_NODE
      )
      const textContent = textNodes
        .map(node => node.textContent)
        .join('')
        .trim()
      additionalProps.channelName =
        textContent || targetElement.textContent || targetElement.innerHTML
    }

    parentElement?.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
        detail: {
          parent: {
            left: linkRects?.item(0)?.left,
            right: linkRects?.item(0)?.right,
            top: linkRects?.item(0)?.top,
            bottom: linkRects?.item(0)?.bottom,
            height: linkRects?.item(0)?.height,
            width: linkRects?.item(0)?.width,
            parentElementRect: parentElementRect,
          },
          ...additionalProps,
        },
      })
    )
  }
}

export function closeToolTipElement(
  parentElement: Element | null | undefined,
  type: MentionType
): void {
  let eventName = 'close-tooltip'
  if (type === MentionType.CHANNEL_MENTION) {
    eventName = 'close-private-channel-tooltip'
  }
  if (type === MentionType.EVENTS_MENTION) {
    eventName = 'close-event-tooltip'
  }
  if (type === MentionType.COURSES_MENTION) {
    eventName = 'close-course-tooltip'
  }
  parentElement?.dispatchEvent(new CustomEvent(eventName, {}))
}

export function closeAllMentionsTooltips(): void {
  const channelTooltipElement = document.getElementById(
    'channel-details-tooltip'
  )
  if (channelTooltipElement) {
    channelTooltipElement.dispatchEvent(
      new CustomEvent('close-private-channel-tooltip', {})
    )
  }

  const eventTooltipElement = document.getElementById('event-tooltip')
  if (eventTooltipElement) {
    eventTooltipElement.dispatchEvent(
      new CustomEvent('close-event-tooltip', {})
    )
  }
  const userTooltipElement = document.getElementById('user_profile_tooltip')
  if (userTooltipElement) {
    userTooltipElement.dispatchEvent(new CustomEvent('close-tooltip', {}))
  }
}

const mentionEventHandlers = new WeakMap<
  Element,
  { mouseover?: Function; mouseout?: Function; click?: Function }
>()

const addEventsToUserMentions = (
  anchor: Element,
  mentionUserTooltipElement: Element | null | undefined,
  router: Router
) => {
  // if its gokollab then update the href to the user profile page
  let slug
  if (anchor.getAttribute('data-slug')) {
    slug = anchor.getAttribute('data-slug')
  } else {
    // fetch slug from existing anchor href
    slug = anchor['href'].split('/').pop()
  }

  const handlers: {
    mouseover?: Function
    mouseout?: Function
    click?: Function
  } = {}

  if (app.state.isMobile) {
    anchor.setAttribute('target', '_self')
    const clickHandler = (e: Event) => {
      e.preventDefault()
      e.stopPropagation()
      openGlobalUserProfile(slug, router)
    }
    handlers.click = clickHandler
    anchor.addEventListener('click', clickHandler)
  } else {
    anchor['href'] = `${
      !isGokollab() ? '/communities' : ''
    }/users/${slug}?groupId=${GroupState.state.groupData._id}`
  }
  if (!app.state.isMobile) {
    const mouseoverHandler = (event: Event) => {
      showPreviewTooltip(
        event.target as Element,
        mentionUserTooltipElement,
        MentionType.USER_MENTION
      )
    }
    const mouseoutHandler = () => {
      closeToolTipElement(mentionUserTooltipElement, MentionType.USER_MENTION)
    }
    handlers.mouseover = mouseoverHandler
    handlers.mouseout = mouseoutHandler
    anchor.addEventListener('mouseover', mouseoverHandler)
    anchor.addEventListener('mouseout', mouseoutHandler)
  }

  // Store handlers in WeakMap for cleanup
  mentionEventHandlers.set(anchor, handlers)
}

const addEventsToChannelMentions = (anchor: Element, router: Router) => {
  let iconContainer = anchor?.classList?.contains('mention-anchor')
  if (iconContainer) {
    return
  }

  // Handle permission
  const channelId = anchor.getAttribute('data-id')
  const channel = GroupState.state.channelList?.find(
    channel => channel._id === channelId
  )
  const mentionChannelTooltipElement = document.getElementById(
    'channel-details-tooltip'
  )

  const handlers: {
    mouseover?: Function
    mouseout?: Function
    click?: Function
  } = {}

  anchor.setAttribute('target', '_self')

  const clickHandler = (e: Event) => {
    e.preventDefault()
    e.stopPropagation()
    if (channel) {
      setTimeout(() => {
        closeToolTipElement(
          mentionChannelTooltipElement,
          MentionType.CHANNEL_MENTION
        )
      }, 200)
      router.push({
        name: 'channel-overview',
        params: {
          channelSlug: channel.slug,
        },
      })
    } else {
      showPreviewTooltip(
        anchor,
        mentionChannelTooltipElement,
        MentionType.CHANNEL_MENTION
      )
    }
  }
  handlers.click = clickHandler
  anchor.addEventListener('click', clickHandler)

  // handle icon if channel data is loaded show latest icon otherwise show the icon from the anchor
  const icon = channel?.icon || anchor.getAttribute('data-icon')
  if (icon && typeof icon === 'string') {
    // Remove # from text
    const currentText = anchor.textContent || ''
    const cleanText = currentText.startsWith('#')
      ? currentText.substring(1)
      : currentText

    // Generate icon HTML
    const iconHTML = generateChannelIconHTML(icon, 16)
    let iconContainer = anchor.querySelector('.channel-icon') // a node to store the icon

    if (!iconContainer) {
      anchor.classList.add(
        'mention-anchor',
        'flex',
        'items-center',
        'gap-[1px]',
        'leading-[1.3]'
      )
      anchor.innerHTML = ''
      const textNode = document.createElement('span')
      textNode.textContent = cleanText

      textNode.className = 'inline-block align-baseline'
      const sanitizedIconHTML = DOMPurify.sanitize(iconHTML)
      anchor.insertAdjacentHTML('beforeend', sanitizedIconHTML)
      anchor.appendChild(textNode)
    }
  }

  // Popups should be shown only on web
  if (!app.state.isMobile) {
    const mouseoverHandler = () => {
      showPreviewTooltip(
        anchor as Element,
        mentionChannelTooltipElement,
        MentionType.CHANNEL_MENTION
      )
    }
    const mouseoutHandler = () => {
      closeToolTipElement(
        mentionChannelTooltipElement,
        MentionType.CHANNEL_MENTION
      )
    }

    handlers.mouseover = mouseoverHandler
    handlers.mouseout = mouseoutHandler
    anchor.addEventListener('mouseover', mouseoverHandler)
    anchor.addEventListener('mouseout', mouseoutHandler)

    // Store handlers in WeakMap for cleanup
    mentionEventHandlers.set(anchor, handlers)
  }
}

// Helper functions for event tooltip
export function showEventTooltip(
  targetElement: Element | null,
  parentElement: Element | null,
  type: 'loading' | 'error' | 'success',
  errorMessage?: string,
  eventData?: any
): void {
  if (targetElement && parentElement) {
    const parentElementRect = targetElement?.getBoundingClientRect()
    const linkRects = targetElement?.getClientRects()

    let eventName = 'show-event-loading-tooltip'
    const additionalProps: any = {
      eventName: targetElement?.getAttribute('data-title') || '',
    }

    if (type === 'error') {
      eventName = 'show-event-error-tooltip'
      additionalProps.errorMessage = errorMessage
    }
    if (type === 'success') {
      eventName = 'show-event-success-tooltip'
      additionalProps.eventName = eventName
      additionalProps.eventData = eventData
    }

    parentElement?.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
        detail: {
          parent: {
            left: linkRects?.item(0)?.left,
            right: linkRects?.item(0)?.right,
            top: Number(linkRects?.item(0)?.top),
            height: linkRects?.item(0)?.height,
            width: linkRects?.item(0)?.width,
            parentElementRect: parentElementRect,
          },
          ...additionalProps,
        },
      })
    )
  }
}

// Helper functions for course tooltip
export function showCourseTooltip(
  targetElement: Element | null,
  parentElement: Element | null,
  type: 'loading' | 'error' | 'success',
  errorMessage?: string,
  courseName?: string
): void {
  if (targetElement && parentElement) {
    const parentElementRect = targetElement?.getBoundingClientRect()
    const linkRects = targetElement?.getClientRects()

    let eventName = 'show-course-loading-tooltip'
    const additionalProps: any = {
      courseName: targetElement?.getAttribute('data-title') || '',
    }

    if (type === 'error') {
      eventName = 'show-course-error-tooltip'
      additionalProps.errorMessage = errorMessage
    }
    if (type === 'success') {
      eventName = 'show-course-success-tooltip'
      additionalProps.courseName = courseName
    }

    parentElement?.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
        detail: {
          parent: {
            left: linkRects?.item(0)?.left,
            right: linkRects?.item(0)?.right,
            top: Number(linkRects?.item(0)?.top),
            height: linkRects?.item(0)?.height,
            width: linkRects?.item(0)?.width,
            parentElementRect: parentElementRect,
          },
          ...additionalProps,
        },
      })
    )
  }
}

const handleEventClick = async (e: Event, anchor: Element, router?: Router) => {
  e.preventDefault()
  e.stopPropagation()

  const eventId = anchor.getAttribute('data-id')
  const tooltipElement = document.getElementById('event-tooltip')
  // Show tooltip on web only
  if (!app.state.isMobile) {
    showEventTooltip(anchor, tooltipElement, 'loading')
  }

  try {
    // Check if event is accessible to user
    const occurrenceDate = Number(anchor.getAttribute('data-timestamp'))
    let event = EventState.state.events.find(event => {
      if (event.isRecurring) {
        const occurrenceDate = anchor.getAttribute('data-timestamp')
        const eventDate = occurrenceDate
          ? new Date(Number(occurrenceDate)).toString()
          : new Date(event.startTime).toString()
        return event.occurrenceDate === eventDate
      }
      return event.eventId === eventId
    })

    // if not cached, fetch from server
    if (!event) {
      event = await EventState.fetchEventById(eventId as string, {
        occurrenceDate,
      })
    }
    if (event === 'not-accessible' || event === 'deleted') {
      if (!app.state.isMobile) {
        showEventTooltip(anchor, tooltipElement, 'error', event)
        return
      }
    }
    if (event && event.isRecurring) {
      event.startTime = new Date(occurrenceDate).toString()
    }
    if (!app.state.isMobile) {
      showEventTooltip(anchor, tooltipElement, 'success', event.title, event)
    }

    const markedTimestamp = anchor.getAttribute('data-timestamp')
    const eventDate = markedTimestamp
      ? new Date(Number(markedTimestamp)).toString()
      : new Date().toString()

    if (router) {
      // Hide tooltip and navigate on success
      closeToolTipElement(tooltipElement, MentionType.EVENTS_MENTION)
      router.push({
        name: 'events',
        query: {
          eventId,
          eventDate,
        },
      })
    }
  } catch {
    showEventTooltip(anchor, tooltipElement, 'error')
  }
}

const addEventsToEventsMentions = (anchor: Element, router: Router) => {
  if (anchor.classList.contains('mention-anchor')) {
    return
  }
  const tooltipElement = document.getElementById('event-tooltip')
  const currentText = anchor.textContent || ''
  const cleanText = currentText.startsWith('#')
    ? currentText.substring(1)
    : currentText

  // Generate icon HTML similar to channel mentions
  let iconContainer = anchor.querySelector('.channel-icon') // a node to store the icon

  if (!iconContainer) {
    // Add similar classes as channel mentions
    anchor.classList.add(
      'mention-anchor',
      'inline-flex',
      'items-center',
      'gap-[1px]',
      'leading-[1.3]',
      'max-w-[95%]'
    )

    anchor.innerHTML = ''
    const tempDiv = document.createElement('div')
    const vnode = h(CalendarDateIcon, {
      class: 'h-[16px] w-[16px] text-current flex-shrink-0',
    })
    render(vnode, tempDiv)
    const iconHTML = tempDiv.innerHTML

    // Apply baseline alignment
    const finalIconHTML = applyBaselineAlignment(iconHTML)

    const textNode = document.createElement('span')
    textNode.textContent = cleanText
    textNode.className = 'truncate min-w-0 flex-1'
    const sanitizedIconHTML = DOMPurify.sanitize(finalIconHTML)
    anchor.insertAdjacentHTML('beforeend', sanitizedIconHTML)
    anchor.appendChild(textNode)
  }

  const markedTimestamp = anchor.getAttribute('data-timestamp')
  const eventDate = markedTimestamp
    ? new Date(Number(markedTimestamp)).toString()
    : new Date().toString()

  anchor['href'] = getEventUrl(
    GroupState.state.groupData.slug,
    anchor.getAttribute('data-id') || '',
    eventDate
  )
  anchor.addEventListener('click', async (e: Event) => {
    handleEventClick(e, anchor, router)
  })
  if (!app.state.isMobile) {
    anchor.addEventListener('mouseover', (e: Event) => {
      handleEventClick(e, anchor)
    })
    anchor.addEventListener('mouseout', () => {
      closeToolTipElement(tooltipElement, MentionType.EVENTS_MENTION)
    })
  }
}

const handleCourseClick = async (
  e: Event,
  anchor: Element,
  router?: Router
) => {
  e.preventDefault()
  e.stopPropagation()

  const courseId = anchor.getAttribute('data-id')
  const tooltipElement = document.getElementById('course-tooltip')
  // Show tooltip on web only
  if (!app.state.isMobile) {
    showCourseTooltip(anchor, tooltipElement, 'loading')
  }

  try {
    const course = await LearningState.fetchCourseById(courseId as string)

    if (course === 'not-accessible' || course === 'deleted') {
      if (!app.state.isMobile) {
        showCourseTooltip(anchor, tooltipElement, 'error', course)
        return
      }
    }
    if (!app.state.isMobile && typeof course === 'object') {
      const courseTitle = anchor.getAttribute('data-label') || course.title
      showCourseTooltip(anchor, tooltipElement, 'success', '', courseTitle)
    }

    if (router) {
      // Hide tooltip and navigate on success
      closeToolTipElement(tooltipElement, MentionType.COURSES_MENTION)
      router.push({
        name: 'learning-home',
        query: {
          productId: courseId,
        },
      })
    }
  } catch (error) {
    // Show error tooltip
    console.log('error in course tooltip', error)
    showEventTooltip(anchor, tooltipElement, 'error')
  }
}

function addEventsToCoursesMentions(anchor: Element, router: Router) {
  if (anchor.classList.contains('mention-anchor')) {
    return
  }
  const tooltipElement = document.getElementById('course-tooltip')
  const currentText = anchor.textContent || ''
  const cleanText = currentText.startsWith('#')
    ? currentText.substring(1)
    : currentText

  // Generate icon HTML similar to channel mentions
  let iconContainer = anchor.querySelector('.channel-icon') // a node to store the icon

  if (!iconContainer) {
    // Add similar classes as channel mentions
    anchor.classList.add(
      'mention-anchor',
      'inline-flex',
      'items-center',
      'gap-[1px]',
      'leading-[1.3]',
      'max-w-[95%]'
    )

    anchor.innerHTML = ''
    const tempDiv = document.createElement('div')
    const vnode = h(BookOpen01Icon, {
      class: 'h-[16px] w-[16px] text-current flex-shrink-0',
    })
    render(vnode, tempDiv)
    const iconHTML = tempDiv.innerHTML

    // Apply baseline alignment
    const finalIconHTML = applyBaselineAlignment(iconHTML)

    const textNode = document.createElement('span')
    textNode.textContent = cleanText
    textNode.className = 'truncate min-w-0 flex-1'
    const sanitizedIconHTML = DOMPurify.sanitize(finalIconHTML)
    anchor.insertAdjacentHTML('beforeend', sanitizedIconHTML)
    anchor.appendChild(textNode)
  }

  anchor['href'] = getCourseUrl(
    GroupState.state.groupData.slug,
    anchor.getAttribute('data-id') || ''
  )
  anchor.addEventListener('click', async (e: Event) => {
    handleCourseClick(e, anchor, router)
  })
  if (!app.state.isMobile) {
    anchor.addEventListener('mouseover', (e: Event) => {
      handleCourseClick(e, anchor)
    })
    anchor.addEventListener('mouseout', () => {
      closeToolTipElement(tooltipElement, MentionType.COURSES_MENTION)
    })
  }
}

export function addEventsToMentions(
  content: HTMLElement | null,
  mentionUserTooltipElement: Element | null | undefined,
  router: Router
): void {
  const mentions = content?.querySelectorAll(
    'a[data-type="mention"], a[data-type="channel-mention"], a[data-type="events-mention"], a[data-type="courses-mention"]'
  )
  if (mentions && mentions.length > 0) {
    mentions.forEach(anchor => {
      const tagType = anchor.getAttribute('data-type')
      if (tagType === 'mention') {
        addEventsToUserMentions(anchor, mentionUserTooltipElement, router)
      } else if (tagType === MentionType.CHANNEL_MENTION) {
        addEventsToChannelMentions(anchor, router)
      } else if (tagType === MentionType.EVENTS_MENTION) {
        addEventsToEventsMentions(anchor, router)
      } else if (tagType === MentionType.COURSES_MENTION) {
        addEventsToCoursesMentions(anchor, router)
      }
    })
  }
}

function removeEventsListenersFromMentions(
  mentionElements: NodeListOf<Element> | null
): void {
  mentionElements?.forEach(anchor => {
    const handlers = mentionEventHandlers.get(anchor)
    if (handlers) {
      if (handlers.mouseover) {
        anchor.removeEventListener(
          'mouseover',
          handlers.mouseover as EventListener
        )
      }
      if (handlers.mouseout) {
        anchor.removeEventListener(
          'mouseout',
          handlers.mouseout as EventListener
        )
      }
      if (handlers.click) {
        anchor.removeEventListener('click', handlers.click as EventListener)
      }
      mentionEventHandlers.delete(anchor)
    }
  })
}

export function removeEventsFromMentions(content: HTMLElement): void {
  const mentions = content?.querySelectorAll(
    'a[data-type="mention"], a[data-type="channel-mention"]'
  )
  if (mentions && mentions.length > 0) {
    removeEventsListenersFromMentions(mentions)
  }
}

type mentionCountMapType = {
  user: { contactId: string }[]
  channel: { channelId: string }[]
  event: { eventId: string }[]
  course: { courseId: string }[]
}

export function getUniqueMentions(content: Document): mentionCountMapType {
  const mentionElements = content.querySelectorAll(
    'a[data-type="mention"], a[data-type="channel-mention"], a[data-type="events-mention"], a[data-type="courses-mention"]'
  )
  const uniqueUserMentions = new Set<string>()
  const uniqueChannelMentions = new Set<string>()
  const uniqueEventMentions = new Set<string>()
  const uniqueCourseMentions = new Set<string>()

  mentionElements.forEach(mention => {
    const tagType = mention.getAttribute('data-type')
    if (tagType === 'mention') {
      const contactId = mention.getAttribute('data-id') || ''
      uniqueUserMentions.add(contactId)
    } else if (tagType === 'channel-mention') {
      const channelId = mention.getAttribute('data-id') || ''
      uniqueChannelMentions.add(channelId)
    } else if (tagType === 'events-mention') {
      const eventId = mention.getAttribute('data-id') || ''
      uniqueEventMentions.add(eventId)
    } else if (tagType === 'courses-mention') {
      const courseId = mention.getAttribute('data-id') || ''
      uniqueCourseMentions.add(courseId)
    }
  })

  return {
    user: Array.from(uniqueUserMentions).map(id => ({ contactId: id })),
    channel: Array.from(uniqueChannelMentions).map(id => ({ channelId: id })),
    event: Array.from(uniqueEventMentions).map(id => ({ eventId: id })),
    course: Array.from(uniqueCourseMentions).map(id => ({ courseId: id })),
  }
}

export function trackMentions(
  type: 'post' | 'comment',
  uniqueMentions: mentionCountMapType
): void {
  if (uniqueMentions.channel.length > 0) {
    trackCustomGAEvents(GAEventTypes.CHANNEL_MENTIONED, {
      count: uniqueMentions.channel.length,
      type,
    })
  }
  if (uniqueMentions.event.length > 0) {
    trackCustomGAEvents(GAEventTypes.EVENT_MENTIONED, {
      count: uniqueMentions.event.length,
      type,
    })
  }
  if (uniqueMentions.course.length > 0) {
    trackCustomGAEvents(GAEventTypes.COURSE_MENTIONED, {
      count: uniqueMentions.course.length,
      type,
    })
  }
}
